// Module-switcher target building + per-tenant module-access resolution.
//
// App/infra glue (not presentation), so it lives in @lumi/app-kit (ADR 0026) rather
// than the headless @lumi/ui — shared as source across the module UIs instead of
// hand-kept copies. The canonical reference is workspace-ui's launcher gate
// (isModuleAccessible) and the peer-routing table below; only the ModuleBadge
// *component* is in @lumi/ui. (workspace-ui keeps its own richer variant that also
// drives cross-module URL building via runtime-config; it is not this file.)
import { LUMI_ICONS, type LumiModuleKey, type ModuleSwitchTarget } from '@lumi/ui';
import type { Component } from 'vue';
import { moduleDescription, moduleName } from './moduleNames';

interface ModuleRoute {
  segment: string;
  devPort: string;
}

// Peer-module routing: prod path segment (modules are path-routed under the shared
// per-tenant origin, `{slug}.{domain}/{segment}`) + local dev port (own dev server).
const MODULE_ROUTING: Record<Exclude<LumiModuleKey, 'suite' | 'admin'>, ModuleRoute> = {
  ats: { segment: 'backoffice', devPort: '5173' },
  time: { segment: 'time', devPort: '5176' },
  apprentice: { segment: 'apprentice', devPort: '5178' },
  people: { segment: 'people', devPort: '5181' },
};

// The workspace itself (the suite homebase, workspace-ui). It is the tenant
// host's ROOT app — no path segment at all in prod, its own dev server locally —
// which is why it is not a MODULE_ROUTING row: that table's entries are all
// `{origin}/{segment}`. The tenant administration pages live here now (#432),
// so this is what the Admin quicklink resolves against.
const WORKSPACE_DEV_PORT = '5170';

/** Session params handed across to the target module (see buildModuleSwitchTargets). */
export type ModuleSwitchParams = Record<string, string | null | undefined>;

// Access/refresh tokens ride the URL ONLY to cross-origin targets (local dev
// ports, e2e docker hosts), where the target app cannot read this origin's
// localStorage. A same-origin peer (prod path routing) reads the shared
// localStorage directly, so tokens in its URL are pure leakage — browser
// history, server logs — which ADR 0024 forbids. Non-secret context
// (tenantSlug, locale, …) is forwarded either way.
//
// `forceTokens` is kept for a caller whose target holds its session under a
// different key and therefore needs the ?token handoff even same-origin. The one
// target that did — the retired tenant admin app — is gone with #432, so nothing
// passes it today; the flag stays because the rule it encodes is the reason this
// function has an opts argument at all.
const TOKEN_PARAMS = new Set(['token', 'refreshToken']);

/**
 * Append the session params to an absolute URL under the rule above. Module-level
 * (rather than a closure inside buildModuleSwitchTargets) so every link this file
 * emits — switch targets AND the workspace billing link — is governed by ONE copy
 * of the token rule; a second copy is a second place to get ADR 0024 wrong.
 *
 * Throws on an unparseable `base`, like the closure it replaces; callers that can
 * be handed an arbitrary string handle that themselves.
 */
export function withSessionParams(
  base: string,
  params: ModuleSwitchParams,
  opts: { forceTokens?: boolean } = {},
): string {
  const url = new URL(base);
  // No window means no origin to compare, so we cannot prove the target is
  // cross-origin — and the token rule must fail CLOSED, not open. (Inside
  // buildModuleSwitchTargets this branch is unreachable: it returns early
  // without a window. It exists for buildWorkspaceBillingHref, which has no
  // such guard, so a non-browser caller strips tokens rather than writing them
  // into a URL nobody proved was cross-origin.)
  const sameOrigin = typeof window === 'undefined' || url.origin === window.location.origin;
  for (const [name, value] of Object.entries(params)) {
    if (!value) continue;
    if (sameOrigin && !opts.forceTokens && TOKEN_PARAMS.has(name)) continue;
    url.searchParams.set(name, value);
  }
  return url.toString();
}

// Merge an in-module deep-link path into a module-switch href WITHOUT dropping the
// SSO handoff query the switcher already put on it (?token&…). The target SPA
// consumes+strips those params on boot, landing the user on `path`. Base href is
// `host:port` (dev) or `origin/segment` (prod); the in-module path is appended to
// the pathname so the peer router (base `/` dev, `/segment` prod) resolves it (M1).
// Shared by Lumi's entity links and the demo journey's "Open in …" link.
export function withDeepLink(baseHref: string, path: string): string {
  try {
    const url = new URL(baseHref);
    const [rawPath, rawQuery] = String(path || '/').split('?');
    const base = url.pathname.replace(/\/+$/, '');
    const rel = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    url.pathname = `${base}${rel}` || '/';
    if (rawQuery) {
      for (const [k, v] of new URLSearchParams(rawQuery)) url.searchParams.set(k, v);
    }
    return url.toString();
  } catch {
    return baseHref;
  }
}

// The workspace billing page is TABBED and its panes are v-show'd, so an anchor
// on it lives inside one pane. A link that names the anchor must also select
// that pane (`?tab=`), or the view lands on whichever tab it picks by default
// and the anchor sits hidden in a pane nobody switched to (issue 383). The pane each
// linkable anchor belongs to, keyed by the bare fragment:
const WORKSPACE_BILLING_ANCHOR_TABS: Record<string, string> = {
  'plan-add': 'plans',
};

/**
 * The workspace's billing page (`/billing` on the workspace origin), for the
 * "upgrade" control on a module teaser. Returns '' for a missing or unparseable
 * workspace URL — the caller renders no control at all rather than a dead link,
 * the same reason buildModuleSwitchTargets skips the workspace entry.
 *
 * Resolved with `new URL('/billing', workspaceHref)` so a workspace URL that
 * carries a path or a query string (session handoff, `?tenant=`) cannot corrupt
 * the target path. Session params follow the same token rule as every other link.
 *
 * `anchor` names a section of the page (`plan-add`, with or without the `#`).
 * The URL carries it as the fragment AND selects the tab pane it lives in (see
 * WORKSPACE_BILLING_ANCHOR_TABS) — BillingView reads `?tab=` on arrival and then
 * scrolls to the fragment once the pane has rendered.
 */
export function buildWorkspaceBillingHref(
  workspaceHref: string | null | undefined,
  params: ModuleSwitchParams,
  anchor?: string,
): string {
  if (!workspaceHref) return '';
  try {
    const url = new URL('/billing', workspaceHref);
    if (anchor) {
      const fragment = anchor.startsWith('#') ? anchor.slice(1) : anchor;
      const tab = WORKSPACE_BILLING_ANCHOR_TABS[fragment];
      if (tab) url.searchParams.set('tab', tab);
      url.hash = fragment;
    }
    return withSessionParams(url.toString(), params);
  } catch {
    return '';
  }
}

/** Hosts served by local dev servers — never path-routed. */
function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '[::1]'
  );
}

/**
 * Where a peer app lives from HERE: locally we keep the current hostname
 * (preserving a tenant subdomain like `tekay.localhost`) and swap the port; in
 * prod we path-route under the current origin. Module-level (rather than a
 * closure inside buildModuleSwitchTargets) so the switcher targets and the
 * account-menu links below resolve a peer through ONE routing rule. Empty
 * without a window: there is no origin to route from, so no link.
 */
function peerBaseUrl({ segment, devPort }: ModuleRoute): string {
  if (typeof window === 'undefined') return '';
  const { hostname, origin, protocol } = window.location;
  return isLocalHost(hostname) ? `${protocol}//${hostname}:${devPort}` : `${origin}/${segment}`;
}

/**
 * The workspace's own origin from HERE — the tenant host root in prod, its dev
 * server locally. Same shape as `peerBaseUrl` above, minus the path segment: the
 * workspace is the root app, so there is none. Empty without a window.
 */
function workspaceBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const { hostname, origin, protocol } = window.location;
  return isLocalHost(hostname) ? `${protocol}//${hostname}:${WORKSPACE_DEV_PORT}` : origin;
}

/**
 * The account menu's Einstellungen link: the person's own preferences — the
 * workspace's `/preferences` (#1069). It used to open tenant administration,
 * which only an admin could use and nobody looked for under "Einstellungen";
 * the workspace's own nav carries Administration.
 *
 * Resolves the workspace origin exactly the way `buildWorkspaceBillingHref`
 * does: same-origin in prod, so the workspace reads the shared session out of
 * localStorage and no tokens go into the URL. Every member has preferences, so
 * nothing gates it. Empty when there is nothing to route from.
 */
export function buildPreferencesHref(params: ModuleSwitchParams = {}): string {
  const base = workspaceBaseUrl();
  return base ? withSessionParams(`${base}/preferences`, params) : '';
}

/**
 * The account menu's Mein Profil link — people-ui's `/me`
 * (issue 401). Routed like any peer module and governed by the same token rule.
 * The CALLER gates it on `people` being in the accessible keys, so the link is
 * never offered to someone the People module would refuse.
 */
export function buildSelfServiceHref(params: ModuleSwitchParams = {}): string {
  const base = peerBaseUrl(MODULE_ROUTING.people);
  return base ? withSessionParams(`${base}/me`, params) : '';
}

/**
 * Where a resolved notification callToAction actually lives, as an absolute URL
 * (#426, #427).
 *
 * The notification row carries only `{ module, deepLink }` — the owning service
 * resolved it (ADR 0021) and has no idea how this browser addresses that app.
 * Turning it into a URL is the same routing problem `buildModuleSwitchTargets`
 * already solves, so it is solved here once rather than in each module UI's
 * notifications popover.
 *
 * It exists because those popovers navigated for exactly ONE module key —
 * `workspace` — and sent every other resolved link to the notifications list
 * instead. The consented-support-access notice (ADR 0041) resolves to `user`, so
 * a tenant admin who was told someone wants into their workspace, and clicked to
 * review it, landed on a list of notifications: the one screen that could not
 * answer the question (#426).
 *
 * The `module` values are the ones the resolvers actually emit, aliases included
 * (`backoffice` for ats, `timecard` for time). An unknown key returns '' — the
 * caller keeps its list fallback, which is the honest answer for a link this app
 * cannot address.
 *
 * `workspaceHref` is the tenant's workspace origin; without it the workspace-owned
 * links (support tickets, billing) have nowhere to point and also return ''.
 */
export function buildCallToActionHref(
  cta: { module?: string | null; deepLink?: string | null } | null | undefined,
  params: ModuleSwitchParams = {},
  workspaceHref?: string | null,
): string {
  const deepLink = String(cta?.deepLink || '').trim();
  if (!deepLink) return '';
  const key = String(cta?.module || '').trim().toLowerCase();
  const path = deepLink.startsWith('/') ? deepLink : `/${deepLink}`;

  const moduleKey = CTA_MODULE_ALIASES[key] ?? key;
  let base = '';
  // `user`/`admin` are the tenant administration pages, which are routes of the
  // workspace since #432 — their deep links resolve against the workspace origin
  // like any other workspace-owned link, and the notification resolvers keep
  // emitting the old module key (see CTA_MODULE_ALIASES' note below).
  if (key === 'workspace' || key === 'suite' || key === 'user' || key === 'admin') {
    base = String(workspaceHref || '').replace(/\/$/, '');
  } else if (Object.prototype.hasOwnProperty.call(MODULE_ROUTING, moduleKey)) {
    // Checked against the routing table rather than cast into it: an unknown key
    // must fall through to '' and the caller's list fallback, not be turned into
    // a bare-origin URL that looks like a link and goes nowhere.
    base = peerBaseUrl(MODULE_ROUTING[moduleKey as keyof typeof MODULE_ROUTING]);
  }
  if (!base) return '';

  try {
    return withSessionParams(base + path, params);
  } catch {
    // An unparseable base (a misconfigured workspace URL) is a dead link, and the
    // caller's fallback is better than one.
    return '';
  }
}

// The module key a callToAction names → the routing entry above. Only the aliases
// need a row; a key that already matches MODULE_ROUTING resolves directly.
const CTA_MODULE_ALIASES: Record<string, string> = {
  backoffice: 'ats',
  timecard: 'time',
};

/**
 * Build the module-switcher target list for the in-module ModuleBadge.
 *
 * Mirrors workspace-ui's `openModule`: each peer app lives on a sibling origin
 * (own dev port locally; same origin but distinct path in prod), so it cannot read
 * this app's per-origin localStorage. We forward the session (token / refreshToken /
 * tenantSlug / locale — whatever the caller supplies) as query params, consumed by
 * the target app on boot. Locally we keep the current hostname (preserving any tenant
 * subdomain like `tekay.localhost`) and swap the port; in prod we path-route under
 * the current origin — avoiding collapsing tenant hosts to bare `localhost` and
 * treating `127.0.0.1` as prod (which 404s on a path dev never serves).
 *
 * The workspace (suite homebase) is always the FIRST entry when `workspaceHref` is
 * supplied, so every module can navigate back to it via the switcher. It's skipped
 * only when there's no resolvable workspace URL (e2e/bare hosts) rather than emit a
 * dead link. The workspace is the homebase, not a gated module, so it is never
 * subject to the `accessible` filter below.
 *
 * `accessible` is the set of modules the user may open in this tenant. When it is
 * null/undefined the access map is UNKNOWN, and we list NO modules — the switcher
 * must never offer a module we can't confirm the user is entitled to.
 *
 * There is no Admin entry any more (#432): tenant administration is not a module
 * and is not an app — it is a section of the workspace, which the switcher's
 * first entry already opens.
 */
export function buildModuleSwitchTargets(
  params: ModuleSwitchParams = {},
  accessible?: LumiModuleKey[] | null,
  workspaceHref?: string | null,
): ModuleSwitchTarget[] {
  if (typeof window === 'undefined') return [];

  // The token rule lives in withSessionParams above (ADR 0024); the peer
  // routing rule lives in peerBaseUrl, shared with the account-menu links.
  const withParams = (base: string, opts: { forceTokens?: boolean } = {}): string =>
    withSessionParams(base, params, opts);
  const baseFor = peerBaseUrl;

  const targets: ModuleSwitchTarget[] = [];

  if (workspaceHref) {
    targets.push({
      key: 'suite',
      label: moduleName('suite'),
      description: moduleDescription('suite'),
      // Home is a place, so its tile wears the house, not the four-squares suite glyph.
      icon: LUMI_ICONS['home'] as Component,
      href: withParams(workspaceHref),
    });
  }

  if (accessible) {
    for (const key of Object.keys(MODULE_ROUTING) as Array<keyof typeof MODULE_ROUTING>) {
      if (!accessible.includes(key)) continue;
      targets.push({
        key,
        label: moduleName(key),
        description: moduleDescription(key),
        href: withParams(baseFor(MODULE_ROUTING[key])),
      });
    }
  }

  return targets;
}

// ── Per-tenant module access (faithful port of workspace-ui's launcher gate) ──

/** Fields of a user-api `GET /tenants` row that decide module access. */
export interface TenantModuleAccess {
  modules?: Array<{ moduleKey: string; status?: string }>;
  // The showroom (#409 M3): the seeder modules this workspace has NOT booked but
  // may open while its demo lasts (user-api's copy of master's declaration).
  // Beside `modules`, never inside: no booking row exists or is implied, and the
  // list is empty the moment the demo ends.
  showroomModules?: string[];
  // `roleKey` is nullable, not optional: user-api emits `roleKey: keys[0] || null`
  // (server.js, GET /api/user/tenants). The narrower `?: string` this used to
  // declare was simply wrong about the wire, and only went unnoticed because
  // nothing here reads the field — every gate below goes through `effect`.
  assignments?: Array<{ moduleKey: string; effect?: string; roleKey?: string | null }>;
  /** Tenant admin flags — the workspace-admin gate (ADR 0033), not a module. */
  isOwner?: boolean;
  isSuiteAdmin?: boolean;
  /** Suite `billing_manager` (ADR 0033) — a subscription gate, not an admin gate:
   *  read by canManageBilling (moduleTeasers.ts), never by hasTenantAdminAccess. */
  isBillingManager?: boolean;
}

// user-api speaks a different module namespace than the switcher (LumiModuleKey).
// user-api's module vocabulary → the suite's registry keys. Exported for the
// demo stage bar, which maps a journey task's module to the `lumiModules.<key>` name
// in the shared catalogue (the registry carries no displayed string).
export const USER_API_TO_LUMI: Record<string, LumiModuleKey> = {
  ats: 'ats',
  time_tracking: 'time',
  apprenticeship: 'apprentice',
  people: 'people',
};

/** Opened by the demo without a booking (#409 M3) — only while listed. */
function isShowroomModule(access: TenantModuleAccess, moduleKey: string): boolean {
  return (access.showroomModules || []).includes(moduleKey);
}

function isModuleActive(access: TenantModuleAccess, moduleKey: string): boolean {
  // A showroom module is open for every member while the demo lasts; the persona
  // sheet, not the booking, decides who sees it (AppChrome intersects the two).
  if (isShowroomModule(access, moduleKey)) return true;
  const mods = access.modules || [];
  if (mods.length === 0) return moduleKey === 'ats';
  return mods.some((m) => m.moduleKey === moduleKey && (m.status === 'active' || m.status === 'trial'));
}

function isModuleAccessible(access: TenantModuleAccess, moduleKey: string): boolean {
  if (!isModuleActive(access, moduleKey)) return false;
  const assignment = (access.assignments || []).find((a) => a.moduleKey === moduleKey);
  if (assignment?.effect === 'deny') return false;
  if (assignment?.effect === 'allow') return true;
  // A showroom module has neither a policy row nor an assignment: the seeder ran
  // the module-side provisioning minus master's status writes, and it is open for
  // everyone while the demo lasts (an explicit deny above still wins, exactly as
  // it does for a booked module).
  if (isShowroomModule(access, moduleKey)) return true;
  // No "nothing configured" fallback any more (ADR 0074 amendment, #457
  // successor): access IS holding an assignment now, so no assignment and no
  // showroom means no access — never "assume everyone can see it". The module
  // default role (`DEFAULT_MODULE_ROLES`) is what used to make this fallback
  // look safe, and it materializes an assignment row at the door instead, so a
  // real member never reaches this branch with nothing to show for it; a
  // role-less member of a workspace this rule postdates does, correctly, get
  // denied.
  return false;
}

/**
 * The LumiModuleKeys the user can open in this tenant — the switcher's candidate
 * set. Returns null when there's no access payload to decide from; callers treat
 * null as "unknown" and list no modules (the switcher never offers unconfirmed
 * access).
 */
/**
 * The switcher keys of the modules a demo persona holds a seat in (#409). While
 * a persona is worn, the module switcher and the launcher offer exactly these —
 * the persona's modules from its sheet, never the real user's access (the sheet
 * is what the module APIs map, so a module missing here would refuse the mask).
 * Modules the sheet names but this UI does not know are dropped, not guessed.
 */
export function personaModuleKeys(persona: { modules: Partial<Record<string, unknown>> } | null | undefined): LumiModuleKey[] {
  if (!persona) return [];
  const out: LumiModuleKey[] = [];
  for (const apiKey of Object.keys(persona.modules)) {
    const lumiKey = USER_API_TO_LUMI[apiKey];
    if (lumiKey && persona.modules[apiKey]) out.push(lumiKey);
  }
  return out;
}

export function accessibleModuleKeys(access: TenantModuleAccess | null | undefined): LumiModuleKey[] | null {

  if (!access) return null;
  const out: LumiModuleKey[] = [];
  for (const [apiKey, lumiKey] of Object.entries(USER_API_TO_LUMI)) {
    if (isModuleAccessible(access, apiKey)) out.push(lumiKey);
  }
  return out;
}

/**
 * The LumiModuleKeys this WORKSPACE has on stage — booked (active or trial) or
 * open as showroom — whatever the signed-in user's own module access says. The
 * set a worn demo persona's seats are intersected with (ADR 0030, persona
 * decision 4 and debt (e)): the sheet decides who sees what, never master access,
 * because every module API resolves the mask on tenant membership alone. A demo
 * guest is a bare membership with no module assignment at all (ADR 0030 revision
 * #669, item 2), so intersecting with `accessibleModuleKeys` left a guest's
 * switcher empty. Never use this outside a worn persona.
 */
export function stageModuleKeys(access: TenantModuleAccess | null | undefined): LumiModuleKey[] | null {
  if (!access) return null;
  const out: LumiModuleKey[] = [];
  for (const [apiKey, lumiKey] of Object.entries(USER_API_TO_LUMI)) {
    if (isModuleActive(access, apiKey)) out.push(lumiKey);
  }
  return out;
}

/**
 * May the user open tenant administration in this tenant? The gate on the
 * workspace's Administration section, and a faithful port of `hasAdminRole`:
 * the workspace OWNER or a `suite_admin` holder (ADR 0033) — exactly the set that
 * passes user-api's `ensureTenantAdmin`, so the entry never leads to a 403.
 *
 * A module `*_admin` (ats_admin, time_admin, …) is deliberately NOT enough: those
 * administer their module, not the workspace (issue 211). Fail-closed — no row
 * (unknown access) means no admin affordance.
 */
export function hasTenantAdminAccess(access: TenantModuleAccess | null | undefined): boolean {
  if (!access) return false;
  return access.isOwner === true || access.isSuiteAdmin === true;
}
